var _ = require('optimizely-client-fn');
var addLazyProperty = require('lazy-property');

var di = require('core/di');
var PromiseEnv = require('core/lib/promise').Promise;

var Constants = require('./constants');
var ControlTower = require('./control_tower');

var BehaviorApi = di.evaluate(require('../api/api'));
if (__TEST__) {
  exports.BehaviorApi = BehaviorApi;
}
var InterestGroupStore = di.get('stores/interest_group');
var TagGroupStore = di.get('stores/tag_group');
var ViewDataStore = di.get('stores/view_data');

/**
 * @param {Object.<InterestGroup>} interestGroups
 *        Map ID -> InterestGroup, whose scores to refresh.
 * @returns {PromiseEnv.<Object, module:ControlTower~AdaGaugeError>}
 *        Rejects with an Error if scores could not be obtained.
 *        Fulfills otherwise with a map InterestGroup ID -> the fresh score for that
 *        InterestGroup, or the default score for all InterestGroups if there is no
 *        event data to base a score on.
 */
exports.fetchScores = function fetchScores(interestGroups:Record<string, InterestGroup>): Promise<any> {
  var tagGroups = _.reduce(interestGroups, function (acc, interestGroup) {
    if (!(interestGroup.tagGroupId in acc)) {
      acc[interestGroup.tagGroupId] = TagGroupStore.getById(interestGroup.tagGroupId);
    }
    return acc;
  }, {});


  var gaugeData = {
    interestGroups: interestGroups,
    tagGroups: tagGroups,
    events: getAdaEvents(interestGroups),
  };

  if (_.isEmpty(gaugeData.events)) {
    // If we don't have any data to base scores off of, don't bother talking to Control Tower.
    // Instead, take this as a signal that anything we might have known about the visitor
    // has effectively aged out, and that their scores ought to regress to the default ones.
    return PromiseEnv.resolve(getDefaultScores(interestGroups));
  }

  return ControlTower.gauge(gaugeData)
    .then(function (responseObj) {
      return responseObj.scores;
    });
};

/**
 * @param {Object.<InterestGroup>} interestGroups
 *        Map ID -> InterestGroup
 * @returns {Array.<module:ControlTower~AdaEvent>}
 *        The minimal set of events that underpin scores for the given InterestGroups.
 *        In the AdaEvents, pages are referred to by ID, tags by apiName.
 * @private
 */
function getAdaEvents(interestGroups:Record<string, InterestGroup>): import('ControlTower').AdaEvent[] {
  var affectingViewIds = {} as any;

  // Array.<TagDescriptor>
  var affectingTags = _.chain(exports.interestGroupsToAffectingTags)
    .pick(_.map(interestGroups, 'id'))
    .values()
    .flatten()
    // Dedupe tag descriptors, equivalent to .uniqWith(_.isEqual)
    .reduce(function (uniqueTags, descriptor) {
      if (!_.some(uniqueTags, _.partial(_.isEqual, descriptor))) {
        uniqueTags.push(descriptor);
        if (!(descriptor.viewId in affectingViewIds)) {
          affectingViewIds[descriptor.viewId] = descriptor.viewId;
        }
      }
      return uniqueTags;
    }, [])
    .value();

  var pageviewEvents = BehaviorApi.query({
    version: '0.2',
    filter: [
      {
        field: ['type'],
        value: 'pageview'
      },
      {
        field: ['name'],
        comparator: 'in',
        value: _.map(affectingViewIds, _.bind(ViewDataStore.idToApiName, ViewDataStore)),
      },
    ],
  });

  // pageview behavior events' `name`s are originally the page's apiName.
  // Most other places in Ada--notably TagDescriptors and the view_activated events in the
  // event backend--address tags and pageview events by the associated page's ID.
  // Converting them here simplifies downstream logic and makes the eventual gauge request
  // use identifiers that are consistent with the rest of the wider system.
  _.forEach(pageviewEvents, function (event) {
    event.name = ViewDataStore.apiNameToId(event.name);
  });

  // An array of events, limited to those that have score-affecting tags. Furthermore, only
  // the score-affecting tags are present.
  var trimmedEvents = [];
  _.forEach(pageviewEvents, function (event) {
    var foundRelevantTags = _.reduce(event.tags, function (acc, value, name) {
      var currTag = { viewId: event.name, tagApiName: name };
      if (_.some(affectingTags, _.partial(_.isEqual, currTag))) {
        acc[name] = value;
      }
      return acc;
    }, {});

    if (_.isEmpty(foundRelevantTags)) {
      return;
    }

    trimmedEvents.push(_.assign({}, event, {
      tags: foundRelevantTags,
    }));
  });

  // Retain MAX_EVENTS number of most recent events.
  var clampedEvents = clampEvents(trimmedEvents);
  var events = _.map(clampedEvents, formatEvent);

  return events;
}

/**
 * Returns the most recent MAX_EVENTS events.
 *
 * @param {Array.<module:behavior/event~Event>} events
 * @returns {Array.<module:behavior/event~Event>}
 * @private
 */
function clampEvents(events) {
  if (_.isEmpty(events)) {
    return events;
  }

  // Sort events, most recent to least recent.
  events = _.sortBy(events, function(e) { return -e.time; });
  return _.slice(events, 0, Constants.MAX_EVENTS);
}

/**
 * @param {module:behavior/event~Event} event
 * @returns {module:ControlTower~AdaEvent}
 */
function formatEvent(event:import('core/behavior/event').Event): import('ControlTower').AdaEvent {
  var textContent = _.reduce(event.tags, function (acc, tagValue, tagName) {
    acc.push({ tag: tagName, value: tagValue.substring(0, Constants.MAX_TAG_LENGTH) });
    return acc;
  }, []);

  return {
    eventName: event.name,
    timestamp: event.time,
    content: {
      text: textContent,
    },
  };
}

/**
 * @param {Object.<InterestGroup>} interestGroups
 * @returns {Object.<number>}
 *        The given InterestGroup's IDs, mapped to the default score.
 * @private
 */
function getDefaultScores(interestGroups:Record<string, InterestGroup>): Record<string, number> {
  return _.mapValues(interestGroups, function () { return Constants.NEUTRAL_SCORE; });
}

/**
 * @return {Object.<Array.<TagDescriptor>>}
 *        Map InterestGroup ID -> tags, observations of which affect that InterestGroup's
 *        score.
 */
function getInterestGroupsToAffectingTags(): Record<string, TagDescriptor[]> {
  var interestGroups = InterestGroupStore.getAll();
  var tagGroups = TagGroupStore.getAll();

  return _.reduce(interestGroups, function (acc, interestGroup) {
    acc[interestGroup.id] = tagGroups[interestGroup.tagGroupId].tags;
    return acc;
  }, {});
}

// This map is relatively expensive to compute, and may be requested often,
// so cache it as a lazy property that's reset if the underlying data changes.
// NOTE: This property will be {} until __DATA__ is loaded, which could be problematic
// in 'split snippet' situations, e.g., https://github.com/optimizely/client-js/pull/674.
function resetInterestGroupsToAffectingTags() {
  addLazyProperty(exports, 'interestGroupsToAffectingTags', getInterestGroupsToAffectingTags);
}
InterestGroupStore.observe(resetInterestGroupsToAffectingTags);
TagGroupStore.observe(resetInterestGroupsToAffectingTags);
// Initialize the lazy property
resetInterestGroupsToAffectingTags();

/**
 * @param {Array.<TagDescriptor>} tags
 * @returns {Object.<InterestGroup>}
 *        Map ID -> InterestGroup, of those whose scores are affected by the given tags.
 */
exports.interestGroupsAffectedByTags = function interestGroupsAffectedByTags(tags:TagDescriptor[]): Record<string, InterestGroup> {
  var containingTagGroupIds = _.chain(TagGroupStore.getAll())
    .filter(function (tagGroup) {
      // For each TagGroup, cross its tags with the given tags. If any are the same,
      // include that TagGroup.
      return _.some(tagGroup.tags, function (t) {
        return _.some(tags, _.partial(_.isEqual, t));
      });
    })
    .reduce(function (acc, tagGroup) {
      // Construct a 'Set'.
      acc[tagGroup.id] = true;
      return acc;
    }, {})
    .value();

  return _.reduce(InterestGroupStore.getAll(), function (acc, ig) {
    if (ig.tagGroupId in containingTagGroupIds) {
      acc[ig.id] = ig;
    }
    return acc;
  }, {});
};
